/* ContractorBlackList.js — «Black List پیمانکاران»
   پنجرهٔ مشترک: از اقلام سند، سرآیند سند، پیمانکاران و قراردادها با پیمانکار جاری باز می‌شود.
   props: contractorCode, contractorName, onClose
   - فهرست محدودیت‌های پیمانکار (تازه‌ترین شروع اول) و «فهرست کلیه محدودیتها»
   - ثبت / ویرایش / حذف؛ رکوردی که کاربر دیگری ساخته فقط با پسورد ذیحساب تغییر می‌کند
   - مبلغ بدون «عنوان کسور» پذیرفته نمی‌شود؛ مانده = مبلغ − کسر شده در حواله‌ها */
import React, { useState, useEffect, useCallback } from 'react';
import JalaliDatePicker from './JalaliDatePicker';
import { useFormButtons } from '../utils/vbPerms';
import { jsonOf, netErr } from '../utils/http';

const toFa = v => String(v ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = v => String(v ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
const money = n => n ? Number(n).toLocaleString('fa-IR') : '•';

const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = {
  contract_id: 0, contract: '', event_date: '', finished_date: '', reason: '', reason2: '',
  amount: '', letter_no: '', letter_date: '', letter_sender: '', deduction_id: 0, deduction: '',
  publish_allowed: false,
};

const COLS = [
  ['user', 'کاربر', 110], ['contract', 'قرارداد', 200], ['letter_no', 'شماره نامه', 90],
  ['letter_date', 'تاریخ نامه', 85], ['letter_sender', 'فرستنده', 130], ['deduction', 'عنوان کسور', 130],
  ['amount', 'مبلغ', 110, 1], ['deducted', 'مبالغ کسر شده', 110, 1], ['remain', 'مانده', 110, 1],
  ['receiver', 'گیرنده', 120], ['account_no', 'شماره حساب', 110], ['pay_id', 'شناسه پرداخت', 100],
  ['bank', 'بانک', 90], ['deduction_kind', 'نوع کسور', 120], ['other_deduction_kind', 'نوع سایر کسور', 100],
  ['event_date', 'شروع', 85], ['reason', 'توضیحات شروع محدودیت', 180], ['finished_date', 'خاتمه', 85],
  ['reason2', 'توضیحات خاتمه محدودیت', 180], ['is_in_black_list', 'محدودیت', 60],
  ['publish_allowed', 'قابل نمایش در سامانه ارجان پیمان', 90],
];

function Pick({ label, value, field, contractor, onPick, disabled }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (t) => {
    setQ(t);
    const j = await API(`/black-list/lookup/?field=${field}&contractor=${contractor || 0}&q=${encodeURIComponent(toEng(t))}`);
    if (j.ok) {
      if (j.data.length === 1 && t) { onPick(j.data[0]); setOpen(false); setQ(''); return; }
      setRows(j.data); setOpen(true);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, position: 'relative' }}>
      <label style={st.lbl}>{label}</label>
      <input value={value || q} disabled={disabled} onChange={e => { onPick({ id: 0, label: '' }); search(e.target.value); }}
        onDoubleClick={() => !disabled && search('')} placeholder="جستجو یا دابل‌کلیک"
        onKeyDown={e => { if (e.key === 'Delete') { onPick({ id: 0, label: '' }); setQ(''); } }}
        style={{ ...st.inp, background: disabled ? '#f3f3f3' : '#fff' }} />
      {open && rows.length > 0 && (
        <div style={st.drop}>
          {rows.map(r => (
            <div key={r.id} style={st.dropRow} onClick={() => { onPick(r); setOpen(false); setQ(''); }}>
              <b>{toFa(r.label)}</b> {r.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContractorBlackList({ contractorCode, contractorName, onClose }) {
  const vbf = useFormButtons('FRMCONTRACTORBLACKLIST',
    ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNALLRECORDS']);
  const [rows, setRows] = useState([]);
  const [all, setAll] = useState(false);
  const [name, setName] = useState(contractorName || '');
  const [me, setMe] = useState(null);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState(null);          // null | 'new' | 'edit'
  const [form, setForm] = useState(EMPTY);
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (allRows = all) => {
    setErr('');
    const j = await API(allRows ? '/black-list/?all=1' : `/black-list/?contractor=${contractorCode || 0}`);
    if (!j.ok) { setErr(j.msg || j.error); return; }
    setRows(j.data); setMe(j.me); if (j.contractor_name) setName(j.contractor_name);
    setSel(s => (s && j.data.find(r => r.id === s.id)) || j.data[0] || null);
  }, [contractorCode, all]);
  useEffect(() => { load(false); }, [contractorCode]); // eslint-disable-line

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const foreign = sel && me !== null && sel.people_id !== me;   // رکورد کاربر دیگر

  const startNew = () => { if (!vbf.can('BTNNEW')) return; setForm({ ...EMPTY, event_date: todayFa() }); setMode('new'); setErr(''); };
  const startEdit = () => {
    if (!vbf.can('BTNEDIT') || !sel) return;
    if (foreign && !password) {
      setErr(`این محدودیت توسط \n${sel.user}\n ایجاد شده و تغییر آن توسط کاربر دیگر فقط با پسورد ذیحساب امکانپذیر است`);
      return;
    }
    setForm({ ...EMPTY, ...sel, amount: sel.amount || '' }); setMode('edit'); setErr('');
  };
  const save = async () => {
    if (Number(toEng(form.amount)) > 0 && !form.deduction_id) { setErr('لطفاً نوع کسور را مشخص کنید'); return; }
    if ((form.event_date || '').length !== 10) { setErr('تاریخ شروع محدودیت را وارد کنید'); return; }
    setBusy(true); setErr('');
    const code = mode === 'edit' ? sel.contractor_code : contractorCode;
    const body = JSON.stringify({ ...form, amount: toEng(form.amount), contractor_code: code, password });
    const j = mode === 'new'
      ? await API('/black-list/create/', { method: 'POST', body })
      : await API(`/black-list/${sel.id}/update/`, { method: 'PUT', body });
    setBusy(false);
    if (!j.ok) { setErr(j.msg || j.error); return; }
    setMode(null); setMsg(j.msg); setTimeout(() => setMsg(''), 2500); load();
  };
  const remove = async () => {
    if (!vbf.can('BTNDELETE') || !sel) return;
    if (!window.confirm('آیا از حذف این رکورد مطمئن هستید؟')) return;
    setBusy(true); setErr('');
    const j = await API(`/black-list/${sel.id}/delete/`, { method: 'POST', body: JSON.stringify({ password }) });
    setBusy(false);
    if (!j.ok) { setErr(j.msg || j.error); return; }
    setSel(null); load();
  };
  const toggleAll = () => { const v = !all; setAll(v); load(v); };

  const editing = mode !== null;
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <span style={{ fontWeight: 700 }}>⛔ Black List پیمانکاران {name ? `— ${name}` : ''}</span>
          <button onClick={onClose} style={st.x}>✕</button>
        </div>
        {!vbf.loaded ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>⏳</div>
          : !vbf.open ? <div style={{ padding: 30, textAlign: 'center', color: '#b71c1c' }}>شما دسترسی لازم برای این فرم را ندارید.</div>
          : (
          <>
            <div style={st.bar}>
              {!editing && vbf.can('BTNNEW') && !all && <button style={st.btnNew} onClick={startNew}>＋ ایجاد</button>}
              {!editing && vbf.can('BTNEDIT') && !all && <button style={st.btnEdit} disabled={!sel} onClick={startEdit}>✎ ویرایش</button>}
              {!editing && vbf.can('BTNDELETE') && !all && <button style={st.btnDel} disabled={!sel || busy} onClick={remove}>✕ حذف</button>}
              {!editing && vbf.can('BTNALLRECORDS') && (
                <button style={st.btnAll} onClick={toggleAll}>{all ? 'محدودیت‌های همین پیمانکار' : 'فهرست کلیه محدودیتها'}</button>)}
              {editing && <>
                {vbf.can('BTNOK') && <button style={st.btnNew} disabled={busy} onClick={save}>💾 ثبت</button>}
                <button style={st.btnCancel} onClick={() => { setMode(null); setErr(''); }}>انصراف</button>
              </>}
              {foreign && !all && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto', fontSize: 12 }}>
                  پسورد ذیحساب
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={{ ...st.inp, width: 120 }} />
                </label>)}
            </div>
            {err && <div style={st.err}>⚠️ {err}</div>}
            {msg && <div style={st.ok}>{msg}</div>}

            {editing && (
              <div style={st.form}>
                <div style={st.row}>
                  <Pick label="قرارداد" field="contract" contractor={mode === 'edit' ? sel.contractor_code : contractorCode}
                    value={form.contract} onPick={r => setForm(f => ({ ...f, contract_id: r.id, contract: r.label }))} />
                  <div style={st.col}><label style={st.lbl}>تاریخ شروع محدودیت</label>
                    <JalaliDatePicker value={form.event_date} onChange={v => F('event_date', v)} /></div>
                  <div style={st.col}><label style={st.lbl}>تاریخ خاتمه محدودیت</label>
                    <JalaliDatePicker value={form.finished_date} onChange={v => F('finished_date', v)} /></div>
                </div>
                <div style={st.row}>
                  <div style={{ ...st.col, flex: 2 }}><label style={st.lbl}>توضیحات (شروع)</label>
                    <input style={st.inp} value={form.reason} onChange={e => F('reason', e.target.value)} /></div>
                  <div style={{ ...st.col, flex: 2 }}><label style={st.lbl}>توضیحات (خاتمه)</label>
                    <input style={st.inp} value={form.reason2} onChange={e => F('reason2', e.target.value)} /></div>
                </div>
                <div style={st.row}>
                  <div style={st.col}><label style={st.lbl}>شماره نامه</label>
                    <input style={st.inp} value={form.letter_no} onChange={e => F('letter_no', e.target.value)} /></div>
                  <div style={st.col}><label style={st.lbl}>تاریخ </label>
                    <JalaliDatePicker value={form.letter_date} onChange={v => F('letter_date', v)} /></div>
                  <div style={{ ...st.col, flex: 2 }}><label style={st.lbl}>فرستنده</label>
                    <input style={st.inp} value={form.letter_sender} onChange={e => F('letter_sender', e.target.value)} /></div>
                </div>
                <div style={st.row}>
                  <div style={st.col}><label style={st.lbl}>مبلغ</label>
                    <input style={{ ...st.inp, direction: 'ltr' }} value={toFa(String(form.amount).replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
                      onChange={e => F('amount', toEng(e.target.value).replace(/[^0-9]/g, ''))} /></div>
                  <Pick label="عنوان کسور" field="deduction" value={form.deduction}
                    onPick={r => setForm(f => ({ ...f, deduction_id: r.id, deduction: r.label }))} />
                  <label style={{ ...st.col, flexDirection: 'row', alignItems: 'center', gap: 6, fontSize: 12, paddingTop: 18 }}>
                    <input type="checkbox" checked={!!form.publish_allowed} onChange={e => F('publish_allowed', e.target.checked)} />
                    قابل نمایش در ارجان پیمان
                  </label>
                </div>
              </div>
            )}

            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead><tr>
                  {all && <th style={{ ...st.th, width: 180 }}>پیمانکار</th>}
                  {COLS.map(([k, l, w]) => <th key={k} style={{ ...st.th, width: w }}>{l}</th>)}
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={COLS.length + 1} style={{ padding: 24, textAlign: 'center', color: '#999' }}>محدودیتی ثبت نشده است</td></tr>}
                  {rows.map((r, i) => {
                    const on = sel && sel.id === r.id;
                    return (
                      <tr key={r.id} onClick={() => !editing && setSel(r)}
                        style={{ background: on ? '#1a2f50' : i % 2 ? '#fff' : '#fff8e1', color: on ? '#fff' : '#222', cursor: 'pointer' }}>
                        {all && <td style={st.td}>{r.contractor}</td>}
                        {COLS.map(([k, , , num]) => (
                          <td key={k} style={{ ...st.td, textAlign: num ? 'left' : 'right', direction: num ? 'ltr' : 'rtl' }}>
                            {num ? money(r[k]) : k === 'publish_allowed' ? (r[k] ? 'بلی' : '') : toFa(r[k] || '')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function todayFa() {
  try {
    const p = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date());
    const g = t => (p.find(x => x.type === t) || {}).value || '';
    return `${g('year')}/${g('month')}/${g('day')}`;
  } catch (e) { return ''; }
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(10,22,40,.6)', zIndex: 100500, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl', fontFamily: "'Vazirmatn',Tahoma,sans-serif" },
  modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1200, maxHeight: '92vh', display: 'flex',
    flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 70px rgba(0,0,0,.45)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px',
    background: 'linear-gradient(135deg,#7f0000,#b71c1c)', color: '#fff', fontSize: 14 },
  x: { background: 'transparent', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' },
  bar: { display: 'flex', gap: 8, padding: '10px 14px', borderBottom: '1px solid #eee', flexWrap: 'wrap', alignItems: 'center' },
  btnNew: { padding: '6px 14px', border: 'none', borderRadius: 7, background: '#2e7d32', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  btnEdit: { padding: '6px 14px', border: 'none', borderRadius: 7, background: '#1565c0', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  btnDel: { padding: '6px 14px', border: 'none', borderRadius: 7, background: '#c62828', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  btnAll: { padding: '6px 14px', border: '1px solid #7f0000', borderRadius: 7, background: '#fff', color: '#7f0000', cursor: 'pointer', fontFamily: 'inherit' },
  btnCancel: { padding: '6px 14px', border: '1px solid #ccc', borderRadius: 7, background: '#f5f5f5', cursor: 'pointer', fontFamily: 'inherit' },
  err: { margin: '8px 14px 0', padding: '8px 12px', background: '#ffebee', color: '#b71c1c', borderRadius: 7, fontSize: 12, whiteSpace: 'pre-line' },
  ok: { margin: '8px 14px 0', padding: '8px 12px', background: '#e8f5e9', color: '#1b5e20', borderRadius: 7, fontSize: 12 },
  form: { padding: '10px 14px', borderBottom: '1px solid #eee', background: '#fafafa' },
  row: { display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap' },
  col: { display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 140 },
  lbl: { fontSize: 11, fontWeight: 600, color: '#555' },
  inp: { padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', width: '100%' },
  drop: { position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #ccc', borderRadius: 6,
    maxHeight: 200, overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,.15)' },
  dropRow: { padding: '6px 8px', fontSize: 11, cursor: 'pointer', borderBottom: '1px solid #f0f0f0' },
  tableWrap: { flex: 1, overflow: 'auto', minHeight: 160 },
  table: { borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '100%', fontSize: 11 },
  th: { position: 'sticky', top: 0, background: '#7f0000', color: '#fff', padding: '7px 6px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden' },
  td: { padding: '6px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
};
